import React from "react";
import { Box, Container, Typography, Grid, Card, CardContent } from "@mui/material";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import BuildIcon from "@mui/icons-material/Build";
import CloudIcon from "@mui/icons-material/Cloud";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import CampaignIcon from "@mui/icons-material/Campaign";

const ProjectService = () => {
  const projectServices = [
    {
      title: "Data Science",
      description:
        "AlphaForce specializes in big data, analytics, business intelligence and database solutions that will assure that you have the right information available run your business.",
      icon: AnalyticsIcon,
      bgColor: "#FF4081",
      borderColor: "#FF4081",
    },
    {
      title: "Application Development",
      description:
        "Our application development solutions includes the development of complex and multistage applications, testing, and mobility services using best practice methodologies.",
      icon: BuildIcon,
      bgColor: "#FFC107",
      borderColor: "#FFC107",
    },
    {
      title: "Cloud Services",
      description:
        "Leveraging our data science and app dev solutions in the cloud can provide your organization with the most effective private, hybrid or public cloud that fits your needs.",
      icon: CloudIcon,
      bgColor: "#FF7043",
      borderColor: "#FF7043",
    },
  ];

  const staffingServices = [
    {
      title: "Contract Staffing",
      description:
        "We specialize in high end contract technical staffing to fill your talent gaps in those hard to find technical roles that are critical for your organization's success.",
      icon: LightbulbOutlinedIcon,
      circleBg: "#E3F2FD",
      iconColor: "#1E88E5",
      borderColor: "#1E88E5",
    },
    {
      title: "Direct Hire",
      description:
        "AlphaForce’s expertise in a variety of high end technologies means that you are assured that our experienced recruiters will find the best available candidate on the market today.",
      icon: ShowChartIcon,
      circleBg: "#E8F5E9",
      iconColor: "#43A047",
      borderColor: "#43A047",
    },
    {
      title: "Contract to Hire",
      description:
        "Sometimes you want to try out a consultant in your environment first before considering bringing them on payroll. That’s ok, we can work with you on that.",
      icon: CampaignIcon,
      circleBg: "#F3E5F5",
      iconColor: "#8E24AA",
      borderColor: "#B56AF7",
    },
  ];

  return (
    <Box sx={{ py: { xs: 8, md: 10 }, bgcolor: "#f5f7fa" }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: "center", mb: 2 }}>
          <Typography
            variant="body1"
            sx={{
              fontSize: { xs: "0.95rem", md: "22px" },
              color: "#637282",
              lineHeight: 1.8,
              fontWeight: 800,
              maxWidth: "900px",
              mx: "auto",
              mb: 4,
            }}
          >
            AlphaForce helps find skilled professionals & the right talent in the United States & Canada. We make the match and guarantee it across a range of specializations.
          </Typography>

          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "28px", md: "32px" },
              color: "#0D47A1",
              textTransform: "uppercase",
              letterSpacing: 1,
            }}
          >
            PROJECT SERVICES
          </Typography>
        </Box>

        {/* Service Cards */}
        <Grid container spacing={4}>
          {projectServices.map((service, idx) => {
            const IconComponent = service.icon;
            return (
              <Grid item xs={12} md={4} key={idx}>
                <Card
                  sx={{
                    height: "100%",
                    p: { xs: 2.5, md: 3 },
                    borderRadius: 2,
                    bgcolor: "white",
                    border: "1px solid rgba(0,0,0,0.08)",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                    transition: "all 0.3s ease",
                    position: "relative",
                    display: "flex",
                    flexDirection: "column",
                    "&:hover": {
                      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  {/* Icon Circle */}
                  <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        backgroundColor: service.bgColor,
                        flexShrink: 0,
                      }}
                    >
                      <IconComponent
                        sx={{
                          fontSize: 50,
                          color: "white",
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Content */}
                  <CardContent sx={{ p: 0, textAlign: "center", flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: "1.1rem", md: "1.1rem" },
                        color: "#0D47A1",
                        mb: 2,
                      }}
                    >
                      {service.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        lineHeight: 1.7,
                        color: "#637282",
                        fontSize: { xs: "0.9rem", md: "16px" },
                        mb: 2,
                      }}
                    >
                      {service.description}
                    </Typography>
                  </CardContent>

                  {/* Bottom Colored Border */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      right: 0,
                      height: "6px",
                      backgroundColor: service.borderColor,
                      borderRadius: "0 0 8px 8px",
                    }}
                  />
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Staffing Services Section */}
        <Box sx={{ mt: { xs: 8, md: 10 } }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "28px", md: "32px" },
              color: "#0D1B3E",
              textAlign: "center",
              textTransform: "uppercase",
              letterSpacing: 1,
              mb: { xs: 3, md: 2 },
            }}
          >
            STAFFING SERVICES
          </Typography>

          <Grid container spacing={4}>
            {staffingServices.map((service, idx) => {
              const IconComponent = service.icon;
              return (
                <Grid item xs={12} md={4} key={`staffing-${idx}`}>
                  <Card
                    sx={{
                      height: "100%",
                      // minHeight: { xs: 420, md: 520 },
                      p: { xs: 4, md: 6 },
                      borderRadius: 2,
                      bgcolor: "#FFFFFF",
                      border: "1px solid rgba(0,0,0,0.06)",
                      boxShadow: "0 12px 30px rgba(0,0,0,0.08)",
                      transition: "all 0.3s ease",
                      position: "relative",
                      overflow: "hidden",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      "&:hover": {
                        boxShadow: "0 18px 44px rgba(0,0,0,0.12)",
                        transform: "translateY(-4px)",
                      },
                    }}
                  >
                    {/* Icon */}
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: "50%",
                        backgroundColor: service.circleBg,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mt: { xs: 1, md: 2 },
                        mb: { xs: 3, md: 4 },
                      }}
                    >
                      <IconComponent sx={{ fontSize: 44, color: service.iconColor }} />
                    </Box>

                    {/* Title */}
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: "1.2rem", md: "1.1rem" },
                        color: "#0D1B3E",
                        textAlign: "center",
                        mb: { xs: 2.5, md: 3 },
                      }}
                    >
                      {service.title}
                    </Typography>

                    {/* Description */}
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#6B7785",
                        textAlign: "center",
                        fontWeight: 600,
                        lineHeight: 2,
                        fontSize: { xs: "1rem", md: "16px" },
                        maxWidth: 420,
                        mx: "auto",
                      }}
                    >
                      {service.description}
                    </Typography>

                    {/* Bottom Colored Border */}
                    <Box
                      sx={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: "6px",
                        backgroundColor: service.borderColor,
                      }}
                    />
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default ProjectService;
